import {
  AfterViewInit,
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { NgClass, NgStyle } from '@angular/common';
import { ShadowDomStyleInjectorService } from '@everymatrix-base-components/utils';

@Component({
  selector: 'lib-dm-button-lib',
  template: `
    <button
      class="dm-button"
      [ngStyle]="
        !text
          ? {
              padding:
                size === 'small'
                  ? '4px'
                  : size === 'medium'
                  ? '8px'
                  : size === 'large'
                  ? '12px'
                  : '0px'
            }
          : {}
      "
      [ngClass]="[variant, size, disabled ? 'disabled' : '']"
      [disabled]="disabled"
      (click)="clickHandler($event)"
    >
      <ng-content select="[icon-left]"></ng-content>
      @if(text) {
      <span class="dm-button-label">{{ text }}</span>
      }
      <ng-content select="[icon-right]"></ng-content>
    </button>
  `,
  styleUrl: './dm-button-lib.component.scss',
  standalone: true,
  imports: [NgClass, NgStyle],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.ShadowDom,
})
export class DmButtonLibComponent implements AfterViewInit {
  /**
   * The text displayed on the button.
   */
  @Input() text = '';

  /**
   * Specifies the visual style of the button.
   * Available options:
   * - 'primary-filled'
   * - 'primary-outlined'
   * - 'secondary-filled'
   * - 'secondary-outlined'
   * - 'destructive-filled'
   * - 'destructive-outlined'
   * Default value: 'primary-filled'
   */
  @Input()
  variant:
    | 'primary-filled'
    | 'primary-outlined'
    | 'secondary-filled'
    | 'secondary-outlined'
    | 'destructive-filled'
    | 'destructive-outlined' = 'primary-filled';

  /**
   * Specifies the size of the button.
   * Available options: 'small', 'medium', 'large'
   * Default value: 'medium'
   */
  @Input() size: 'small' | 'medium' | 'large' = 'large';

  /**
   * Disables the button if set to true
   */
  @Input({ transform: booleanAttribute }) disabled = false;

  /**
   * Emits click
   */
  @Output()
  outsideClickEmitter = new EventEmitter<Event>();

  constructor(
    private elementRef: ElementRef,
    private shadowDomStyleInjectorService: ShadowDomStyleInjectorService
  ) {}

  ngAfterViewInit() {
    this.shadowDomStyleInjectorService
      .injectNgPrimeStyles(this.elementRef)
      .subscribe();
  }

  protected clickHandler(event: Event): void {
    this.outsideClickEmitter.emit(event);
    this.elementRef.nativeElement.dispatchEvent(
      new CustomEvent('dm-button-click', {
        bubbles: true,
        composed: true,
      })
    );
  }
}
